import { lazy, Suspense, useEffect } from "react";
import { useAuthStore } from "stores/auth.store";
import "./App.css";
import { CssBaseline, ThemeProvider } from "@mui/material";
import RenderRoutes from "./utils/router";
import appRoutes from "./Routes";
import DataLayer from "./DataLayer";
import { ColorModeContext, useMode } from "./theme";
import LoadingPage from "./components/Fallback/LoadingPage";
import {
  // FORCE_UPDATE_BUILD_NUMBER,
  HOT_REFRESH_SW_VERSION,
  PAGE_URLS,
} from "./utils/constants";
import LoadScripts from "./components/LoadScripts";
import { unregister } from "./serviceWorkerRegistration";
import { useGetServiceWorkerVersionQuery } from "./HomePageDataLayer/HomePageReduxAPI";
import { setUserPropertiesGa } from "./utils/gaUtils";
import FirebaseWrapper from "FirebaseWrapper";
import { FEATURE_FLAGS } from "utils/firebaseFeatureFlags";
import { useFirebaseFeatureFlag } from "hooks/useFirebaseFeatureFlag";
import FTUXBootstrapper from "components/FTUXBootstrapper";
import useViewportResize from "hooks/useViewportResize";
import LoaderRenderer from "components/LoaderRenderer";

// None of these are needed for first paint — code-split them out of the
// main bundle. They mount as soon as their chunk arrives (fallback null),
// so behavior is unchanged apart from a few ms of deferral.
const UnderMaintenance = lazy(() => import("views/UnderMaintenance"));
const AppLazyComp = lazy(() => import("components/AppLazyComp"));
// import DownloadAppNudge from "components/DownloadAppNudge";

// pulltorefreshjs only checks window.scrollY. Pages that scroll inside a
// nested overflow container (e.g. past-payouts, payout-status) keep
// window.scrollY at 0, so any upward scroll was treated as a pull-to-refresh.
// Track the touch target and only allow PTR when that scroll chain is at top.
let lastPtrTouchTarget = null;

const trackPtrTouchTarget = (event) => {
  lastPtrTouchTarget = event.target;
};

const isVerticallyScrollableAwayFromTop = (element) => {
  let node = element instanceof Element ? element : element?.parentElement;
  while (node && node !== document.documentElement) {
    if (node instanceof HTMLElement) {
      const { overflowY } = window.getComputedStyle(node);
      const canScrollY =
        overflowY === "auto" ||
        overflowY === "scroll" ||
        overflowY === "overlay";
      if (
        canScrollY &&
        node.scrollHeight > node.clientHeight + 1 &&
        node.scrollTop > 0
      ) {
        return true;
      }
    }
    node = node.parentElement;
  }
  return false;
};

const shouldPullToRefresh = () => {
  let shouldRefresh =
    !window.scrollY &&
    !(
      lastPtrTouchTarget &&
      isVerticallyScrollableAwayFromTop(lastPtrTouchTarget)
    );
  const allDrawers = Array.from(
    document.getElementsByClassName("MuiDrawer-root")
  );
  const openedDrawer = allDrawers.find(
    (drawer) => !drawer.className.includes("MuiModal-hidden")
  );
  const allModals = Array.from(
    document.getElementsByClassName("MuiModal-root")
  );
  const openedModal = allModals.find(
    (modal) => !modal.className.includes("MuiModal-hidden")
  );
  // Check for opened bottom sheets
  const allBottomSheets = Array.from(
    document.getElementsByClassName("bottom-sheet-wrapper")
  );
  const openedBottomSheet = allBottomSheets.find(
    (sheet) => !sheet.className.includes("bottom-sheet-hidden")
  );
  if (openedDrawer || openedModal || openedBottomSheet) shouldRefresh = false;
  if (
    window.location.pathname === PAGE_URLS.COLLAGE_CREATION ||
    window.location.pathname === PAGE_URLS.ERROR_PAGE ||
    window.location.pathname.startsWith("/copy-links/") ||
    window.location.pathname.endsWith("/preview-links") ||
    window.location.pathname.startsWith("/paid-collaboration-chat/") ||
    window.location.pathname.startsWith("/wishii")
  )
    shouldRefresh = false;
  return shouldRefresh;
};

const HotRefreshServiceWorker = () => {
  const { data: versionData } = useGetServiceWorkerVersionQuery({
    version: HOT_REFRESH_SW_VERSION,
  });
  useEffect(() => {
    if (versionData && versionData.success && versionData.update) {
      unregister();
    }
  }, [versionData]);
  return <></>;
};

function App() {
  const [theme, colorMode] = useMode();
  const { flagValue, isLoading } = useFirebaseFeatureFlag(
    FEATURE_FLAGS.APP_UNDER_MAINTENANCE
  );
  const isUnderMaintenance =
    isLoading === false &&
    typeof flagValue === "string" &&
    flagValue?.toLowerCase() === "true";

  const { isLoggedIn: creatorLoggedIn } = useAuthStore();
  const { windowHeight } = useViewportResize();

  useEffect(() => {
    setUserPropertiesGa();
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (window.ReactNativeWebView) {
      document.addEventListener("touchstart", trackPtrTouchTarget, {
        passive: true,
        capture: true,
      });
      import("pulltorefreshjs").then(({ default: PullToRefresh }) => {
        if (cancelled) {
          PullToRefresh.destroyAll();
          return;
        }
        PullToRefresh.init({
          mainElement: "body",
          shouldPullToRefresh: shouldPullToRefresh,
          refreshTimeout: 100,
          onRefresh: () => window.location.reload(),
          distThreshold: 75,
          distReload: 0,
          distIgnore: 10,
          distMax: 100,
        });
      });
    }
    return () => {
      cancelled = true;
      lastPtrTouchTarget = null;
      if (window.ReactNativeWebView) {
        document.removeEventListener("touchstart", trackPtrTouchTarget, {
          capture: true,
        });
        import("pulltorefreshjs").then(({ default: PullToRefresh }) => {
          PullToRefresh.destroyAll();
        });
      }
    };
  }, []);

  return (
    <>
      <LoaderRenderer />
      <ColorModeContext.Provider value={colorMode}>
        {isUnderMaintenance ? (
          <Suspense fallback={<LoadingPage />}>
            <UnderMaintenance />
          </Suspense>
        ) : (
          <FirebaseWrapper>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              {creatorLoggedIn && <HotRefreshServiceWorker />}
              <FTUXBootstrapper />
              <div className="App" style={{ minHeight: `${windowHeight}px` }}>
                {/* <DownloadAppNudge /> */}
                {/* {isForceUpdateAvailable ? (
                    <AppForceUpdate open={isForceUpdateAvailable} />
                  ) : ( */}
                <>
                  <DataLayer>
                    <RenderRoutes routes={appRoutes} />
                  </DataLayer>
                  <Suspense fallback={null}>
                    <AppLazyComp />
                  </Suspense>
                  <LoadScripts />
                </>
              </div>
            </ThemeProvider>
          </FirebaseWrapper>
        )}
      </ColorModeContext.Provider>
    </>
  );
}

export default App;
